import { useQuery, type ApolloError } from '@apollo/client';
import { useFeatureFlag } from '@newfront-insurance/admin-ui';
import { isEmpty } from '@newfront-insurance/core';
import { createContext, useCallback, useContext, useMemo, type ReactNode } from 'react';

import { useChatOverlayContext } from './chat-overlay-context';
import type { NovaSkill } from '../../../queries/chats';
import { GET_SKILLS_BY_FILTERS } from '../../../queries/chats';

interface SkillLookup {
  command?: string | null;
  skillId?: string | null;
  name?: string | null;
}

interface SkillsContextValue {
  skills: NovaSkill[];
  isLoading: boolean;
  error?: ApolloError;
  findSkill: (lookup: SkillLookup) => NovaSkill | undefined;
  getSkillDescription: (lookup: SkillLookup) => string | undefined;
  refetchSkills: () => Promise<void>;
}

const SkillsContext = createContext<SkillsContextValue | undefined>(undefined);

function normalizeSkillName(name?: string | null) {
  return name?.trim().toLowerCase() ?? '';
}

function normalizeSkillCommand(command?: string | null) {
  return command?.trim().toLowerCase() ?? '';
}

export function SkillsProvider({ children }: { children: ReactNode }) {
  const { userUuid, accountUuid } = useChatOverlayContext();
  const isNovaSkillsEnabled = useFeatureFlag('nova-skills', false);
  const { data, loading, error, refetch } = useQuery(GET_SKILLS_BY_FILTERS, {
    variables: { userUuid, accountUuid },
    skip: !isNovaSkillsEnabled || isEmpty(userUuid),
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-first',
  });

  const skills = useMemo<NovaSkill[]>(
    () => (isNovaSkillsEnabled ? (data?.skills ?? []) : []),
    [isNovaSkillsEnabled, data?.skills],
  );
  const skillsById = useMemo(() => new Map(skills.map((skill) => [skill.id, skill])), [skills]);
  const skillsByCommand = useMemo(
    () =>
      new Map(
        skills
          .map((skill) => {
            const command = normalizeSkillCommand(skill.command);
            return command ? ([command, skill] as const) : null;
          })
          .filter((entry): entry is readonly [string, NovaSkill] => entry !== null),
      ),
    [skills],
  );
  const skillsByName = useMemo(() => new Map(skills.map((skill) => [normalizeSkillName(skill.name), skill])), [skills]);

  const findSkill = useCallback(
    ({ command, skillId, name }: SkillLookup) => {
      const normalizedCommand = normalizeSkillCommand(command);
      if (normalizedCommand) {
        const skill = skillsByCommand.get(normalizedCommand);
        if (skill) {
          return skill;
        }
      }

      if (skillId) {
        const skill = skillsById.get(skillId);
        if (skill) {
          return skill;
        }
      }

      const normalizedName = normalizeSkillName(name);
      return normalizedName ? skillsByName.get(normalizedName) : undefined;
    },
    [skillsByCommand, skillsById, skillsByName],
  );

  const getSkillDescription = useCallback(
    (lookup: SkillLookup) => findSkill(lookup)?.description?.trim() || undefined,
    [findSkill],
  );

  const refetchSkills = useCallback(async () => {
    if (isNovaSkillsEnabled) {
      await refetch();
    }
  }, [isNovaSkillsEnabled, refetch]);

  const value = useMemo<SkillsContextValue>(
    () => ({
      skills,
      isLoading: isNovaSkillsEnabled ? loading : false,
      error: isNovaSkillsEnabled ? (error ?? undefined) : undefined,
      findSkill,
      getSkillDescription,
      refetchSkills,
    }),
    [skills, isNovaSkillsEnabled, loading, error, findSkill, getSkillDescription, refetchSkills],
  );

  return <SkillsContext.Provider value={value}>{children}</SkillsContext.Provider>;
}

export function useSkills(): SkillsContextValue {
  const context = useContext(SkillsContext);
  if (!context) {
    throw new Error('useSkills must be used within SkillsProvider');
  }
  return context;
}

export function useSkillsSafe() {
  return useContext(SkillsContext);
}
